// ── Admin viewport click (god mode) ──
// Extracted verbatim from MapCanvas.tsx: the god-mode pointerdown handler that
// logs world coordinates, clears the green selection when empty space is hit
// and detects a double-click on empty space to open the "create map object"
// type-selection modal.
//
// `AdminClickData`, the `adminClickStartRef` that stores it and the two
// double-click thresholds had NO other consumer in MapCanvas, so they moved
// here with the effect. The body is byte-identical to the original; the
// remaining closure dependencies are parameters under their ORIGINAL names.
import { useEffect, useRef, type MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';
import type { MapObject } from '../../models/mapObject';
import type { UserData } from '../../models/user';
import type { MapModalName, MapModalPayload } from '../../stores/useMapModalStore';
import { asMapMarker } from './render/mapPixiExtensions';

interface AdminClickData {
  x: number;
  y: number;
  worldX: number;
  worldY: number;
  hasMoved: boolean;
  clickedMapObject: MapObject | null;
  timestamp: number; // Для визначення подвійного кліку
}

export interface UseAdminViewportClickParams {
  sceneRef: MutableRefObject<import('./mapSceneState').MapSceneState>;
  clearAdminSelection: () => void;
  setPendingMapObjectCreation: (pos: { x: number; y: number } | null) => void;
  openModal: (name: MapModalName, payload?: Partial<MapModalPayload>) => void;
  /** Effect deps — by value, re-read every render. */
  isAdminMode: boolean;
  userData: UserData | undefined;
}

export function useAdminViewportClick({
  sceneRef,
  clearAdminSelection,
  setPendingMapObjectCreation,
  openModal,
  isAdminMode,
  userData,
}: UseAdminViewportClickParams): void {
  const adminClickStartRef = useRef<AdminClickData | null>(null);
  const DOUBLE_CLICK_THRESHOLD_MS = 500;
  const DOUBLE_CLICK_DISTANCE_THRESHOLD = 20;

  useEffect(() => {
    if (!sceneRef.current.pixi.viewport || !userData?.is_admin) return;

    const viewport = sceneRef.current.pixi.viewport;

    const getPointerType = (event: MouseEvent | PointerEvent): string => {
      if ('pointerType' in event) {
        return event.pointerType || 'mouse';
      }
      return 'mouse';
    };
    
    const isTouch = (event: MouseEvent | PointerEvent): boolean => {
      const pointerType = getPointerType(event);
      return pointerType === 'touch' || pointerType === 'pen';
    };
    
    // Use PixiJS event system instead of manual marker iteration
    const handleViewportPointerDown = (event: PIXI.FederatedPointerEvent) => {
      if (!sceneRef.current.flags.godModeActive) return;
      if (event.button !== 0 && !isTouch(event.nativeEvent as MouseEvent | PointerEvent)) return;

      // Convert screen coords to world coords
      const screenPoint = new PIXI.Point(event.screen.x, event.screen.y);
      const worldPos = viewport.toWorld(screenPoint);
      
      // Walk up parent chain to find mapObjectData (event.target may be a child sprite)
      let clickedMapObject: MapObject | undefined;
      let target = event.target as PIXI.Container | null;
      while (target) {
        const mk = asMapMarker(target as PIXI.Container);
        if (mk?.mapObjectData) {
          clickedMapObject = mk.mapObjectData;
          break;
        }
        target = target.parent;
      }

      // Clear green selection when clicking on empty space
      if (!clickedMapObject) {
        clearAdminSelection();
      }

      const currentTime = Date.now();
      const currentX = event.client.x;
      const currentY = event.client.y;

      // Перевіряємо, чи це подвійний клік
      if (adminClickStartRef.current) {
        const timeDiff = currentTime - adminClickStartRef.current.timestamp;
        const distance = Math.sqrt(
          Math.pow(currentX - adminClickStartRef.current.x, 2) + 
          Math.pow(currentY - adminClickStartRef.current.y, 2)
        );

        // Якщо час між кліками менший за поріг і відстань менша за поріг - це подвійний клік
        if (timeDiff < DOUBLE_CLICK_THRESHOLD_MS && distance < DOUBLE_CLICK_DISTANCE_THRESHOLD) {
          
          // Перевіряємо, чи клікнули на той самий об'єкт (або обидва рази на вільне місце)
          const previousMapObject = adminClickStartRef.current.clickedMapObject;
          const isSameObject = (previousMapObject === null && clickedMapObject === undefined) ||
                              (previousMapObject !== null && clickedMapObject !== undefined && 
                               previousMapObject.id === clickedMapObject.id);

          if (isSameObject) {
            const finalWorldPos = { x: worldPos.x, y: worldPos.y };

            if (!clickedMapObject) {
              // Double-click on empty space — create new object
              setPendingMapObjectCreation(finalWorldPos);
              openModal('typeSelection');
            }

            adminClickStartRef.current = null;
            return;
          }
        }
      }

      // Зберігаємо дані про поточний клік для можливого подвійного кліку
      adminClickStartRef.current = {
        x: currentX,
        y: currentY,
        worldX: worldPos.x,
        worldY: worldPos.y,
        hasMoved: false,
        clickedMapObject: clickedMapObject || null,
        timestamp: currentTime
      };

      // Встановлюємо таймер для скидання посилання, якщо не буде другого кліку
      setTimeout(() => {
        if (adminClickStartRef.current && adminClickStartRef.current.timestamp === currentTime) {
          adminClickStartRef.current = null;
        }
      }, DOUBLE_CLICK_THRESHOLD_MS);
    };

    viewport.eventMode = 'static';
    viewport.on('pointerdown', handleViewportPointerDown);

    return () => {
      viewport.off('pointerdown', handleViewportPointerDown);
      adminClickStartRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- rebind on admin/user gate; handlers via live refs
  }, [isAdminMode, userData]);
}
